/**
 * Preferences slice (issue #424) — theme, locale, timezone, dashboardLayout.
 *
 * Initial state is seeded from the browser: theme from the localStorage mirror
 * (so it matches the pre-paint bootstrap), locale from `navigator.language`,
 * timezone from the resolved Intl zone. The authoritative server values
 * (loaded via GraphQL on mount) overwrite these via `setPreferences`.
 *
 * `setTheme` applies the theme to `<html data-theme>` as a side effect so the
 * switch takes effect immediately and persists to localStorage.
 *
 * `dashboardLayout` (issue #451) is saved optimistically via the
 * `dashboardLayoutSaveStarted/Succeeded/Failed` actions, orchestrated by the
 * `saveDashboardLayout` helper in `hooks/useDashboardLayoutSave.ts` (kept out
 * of this file deliberately: `lib/apiClient.ts` imports the `store` singleton
 * to read the auth token, and this slice is part of the store's reducer
 * graph — importing apiClient here would create a circular import).
 * `dashboardLayoutSaveStarted` applies the new layout immediately,
 * snapshotting the prior layout; on success the snapshot is cleared, on
 * failure it is restored and an error is recorded, so the grid visually
 * reverts rather than keeping a layout the server never persisted.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { applyTheme, readStoredTheme, type ThemeMode } from '../../../lib/themeBootstrap';
import { backfillInstanceIds } from '../../../lib/dashboardLayout';
import type { DashboardLayout } from '../../../types/preferences';
import type { RequestStatus, ErrorState } from '../../types';

export type { ThemeMode };

export interface PreferencesState {
  theme: ThemeMode;
  locale: string;
  timezone: string;
  /** True once the browser locale has been detected/seeded. */
  localeDetected: boolean;
  dashboardLayout: DashboardLayout | null;
  /**
   * Snapshot of `dashboardLayout` taken when an optimistic save begins.
   * Non-null only while a save is in flight; restored to `dashboardLayout`
   * and cleared on failure, simply cleared on success.
   */
  previousDashboardLayout: DashboardLayout | null;
  status: RequestStatus;
  error: ErrorState | null;
}

/** Detect the browser's preferred BCP-47 locale, defaulting to en-US. */
export function detectBrowserLocale(): string {
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    return navigator.language || 'en-US';
  }
  return 'en-US';
}

/** Resolve the browser's IANA timezone, defaulting to UTC. */
export function detectBrowserTimezone(): string {
  if (typeof Intl !== 'undefined') {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }
  return 'UTC';
}

const initialState: PreferencesState = {
  theme: readStoredTheme(),
  locale: detectBrowserLocale(),
  timezone: detectBrowserTimezone(),
  localeDetected: false,
  dashboardLayout: null,
  previousDashboardLayout: null,
  status: 'idle',
  error: null,
};

export interface PreferencesPayload {
  theme?: ThemeMode;
  locale?: string;
  timezone?: string;
  dashboardLayout?: DashboardLayout;
}

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
      applyTheme(action.payload);
    },
    setLocale: (state, action: PayloadAction<string>) => {
      state.locale = action.payload;
      state.localeDetected = true;
    },
    setTimezone: (state, action: PayloadAction<string>) => {
      state.timezone = action.payload;
    },
    /**
     * Apply authoritative values (typically loaded from the server). Only
     * provided fields are updated; the theme side effect is applied too.
     */
    setPreferences: (state, action: PayloadAction<PreferencesPayload>) => {
      const { theme, locale, timezone, dashboardLayout } = action.payload;
      if (theme !== undefined) {
        state.theme = theme;
        applyTheme(theme);
      }
      if (locale !== undefined) {
        state.locale = locale;
        state.localeDetected = true;
      }
      if (timezone !== undefined) {
        state.timezone = timezone;
      }
      if (dashboardLayout !== undefined) {
        // Legacy (pre-#1260) layouts have no `instanceId`; backfill before
        // anything downstream reads the store so the field can be relied on
        // as always present (see ADR 1260).
        state.dashboardLayout = backfillInstanceIds(dashboardLayout);
      }
    },
    /** Mark the browser-detected locale as captured (first-visit detection). */
    markLocaleDetected: (state) => {
      state.localeDetected = true;
    },
    setPreferencesLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    setPreferencesSucceeded: (state) => {
      state.status = 'succeeded';
      state.error = null;
    },
    setPreferencesError: (state, action: PayloadAction<ErrorState>) => {
      state.status = 'failed';
      state.error = action.payload;
    },
    /** Replace the dashboard layout with no optimistic-save bookkeeping (e.g. server hydration). */
    setDashboardLayout: (state, action: PayloadAction<DashboardLayout>) => {
      state.dashboardLayout = backfillInstanceIds(action.payload);
    },
    /**
     * Begin an optimistic save: snapshot the current layout (for rollback)
     * and apply the new layout immediately, before the REST call resolves.
     */
    dashboardLayoutSaveStarted: (state, action: PayloadAction<DashboardLayout>) => {
      state.previousDashboardLayout = state.dashboardLayout;
      state.dashboardLayout = action.payload;
      state.error = null;
    },
    /** REST save succeeded — the optimistic layout is confirmed; clear the rollback snapshot. */
    dashboardLayoutSaveSucceeded: (state) => {
      state.previousDashboardLayout = null;
    },
    /** REST save failed — restore the pre-save layout and record the error. */
    dashboardLayoutSaveFailed: (state, action: PayloadAction<ErrorState>) => {
      if (state.previousDashboardLayout !== null) {
        state.dashboardLayout = state.previousDashboardLayout;
        state.previousDashboardLayout = null;
      }
      state.error = action.payload;
    },
  },
});

export const {
  setTheme,
  setLocale,
  setTimezone,
  setPreferences,
  markLocaleDetected,
  setPreferencesLoading,
  setPreferencesSucceeded,
  setPreferencesError,
  setDashboardLayout,
  dashboardLayoutSaveStarted,
  dashboardLayoutSaveSucceeded,
  dashboardLayoutSaveFailed,
} = preferencesSlice.actions;

export default preferencesSlice.reducer;

// Selectors
export const selectTheme = (state: { preferences: PreferencesState }): ThemeMode =>
  state.preferences.theme;
export const selectLocale = (state: { preferences: PreferencesState }): string =>
  state.preferences.locale;
export const selectTimezone = (state: { preferences: PreferencesState }): string =>
  state.preferences.timezone;
export const selectPreferences = (state: { preferences: PreferencesState }): PreferencesState =>
  state.preferences;
export const selectPreferencesStatus = (state: { preferences: PreferencesState }): RequestStatus =>
  state.preferences.status;
export const selectDashboardLayout = (state: {
  preferences: PreferencesState;
}): DashboardLayout | null => state.preferences.dashboardLayout;
